import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CatalogMenu } from "./CatalogMenu";

// Pixel offset applied to the scrollspy: header height (~220-400px depending
// on breakpoint) + small breathing room so a section is considered "active"
// slightly before its top edge crosses the viewport top.
const SCROLLSPY_OFFSET_PX = 480;
// Fixed-header transitions to its solid background after this scroll Y.
const HEADER_SOLID_THRESHOLD_PX = 20;
// Distance from the page bottom that forces the "contact" link to be active.
const PAGE_BOTTOM_BUFFER_PX = 80;

const LogoMark = ({ large = false, shrunk = false }) => {
  // When user starts scrolling on home, gradually shrink the oversized hero
  // logo so it doesn't keep eating vertical space. Other pages already use
  // the smaller size — only the `large` (home) branch needs to react.
  let sizeClasses = "h-14 sm:h-16 md:h-32 lg:h-40";
  if (large) {
    sizeClasses = shrunk
      ? "h-14 sm:h-16 md:h-24 lg:h-32"
      : "h-20 sm:h-24 md:h-44 lg:h-56";
  }
  return (
    <Link
      to="/"
      data-testid="brand-logo"
      className="group inline-flex shrink-0 items-center"
    >
      <img
        src="/logo-tvcabsat.png"
        alt="TVCabSat"
        className={`logo-animated w-auto max-w-none shrink-0 object-contain transition-[height] duration-300 ease-out ${sizeClasses}`}
      />
    </Link>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Smart anchor handler: if already on the home page just scroll, otherwise
  // navigate to home and let the home-page hash effect scroll on mount.
  const goToAnchor = (e, anchorId) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(anchorId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `/#${anchorId}`);
    } else {
      navigate(`/#${anchorId}`);
    }
  };

  useEffect(() => {
    const onScroll = () =>
      setScrolled(window.scrollY > HEADER_SOLID_THRESHOLD_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy: highlight the nav link whose section is currently in view.
  // Uses scroll position relative to a fixed offset (the header height +
  // a bit). Brand showcases (intelbras/motorola/...) all map back to the
  // "partners" link.
  const [activeAnchor, setActiveAnchor] = useState("");
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveAnchor("");
      return undefined;
    }
    // Map of section id → which nav link should light up
    const sectionMap = [
      { id: "about", spy: "about" },
      { id: "services", spy: "services" },
      { id: "partners", spy: "partners" },
      { id: "intelbras", spy: "partners" },
      { id: "motorola", spy: "partners" },
      { id: "hytera", spy: "partners" },
      { id: "cisco", spy: "partners" },
      { id: "alcatel", spy: "partners" },
      { id: "dstv", spy: "partners" },
      { id: "vsat", spy: "partners" },
      { id: "whyus", spy: "partners" },
      { id: "contact", spy: "contact" },
    ];

    const onScroll = () => {
      const y = window.scrollY + SCROLLSPY_OFFSET_PX;
      // bottom of page → force "contact"
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - PAGE_BOTTOM_BUFFER_PX
      ) {
        setActiveAnchor("contact");
        return;
      }
      let current = "";
      for (const { id, spy } of sectionMap) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        if (y >= top) current = spy;
      }
      setActiveAnchor(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  const isActive = (l) => {
    if (l.type === "route") return location.pathname === l.href;
    return activeAnchor === l.id;
  };

  const isHome = location.pathname === "/";

  const links = [
    { href: "/#about", label: t.nav.about, type: "anchor", id: "about" },
    { href: "/equipamentos", label: t.nav.equipment, type: "route", id: "equipment" },
    { href: "/infraestruturas", label: t.nav.infrastructure, type: "route", id: "infrastructure" },
    { href: "/portfolio", label: t.nav.portfolio, type: "route", id: "portfolio" },
    { href: "/#services", label: t.nav.services, type: "anchor", id: "services" },
    { href: "/#partners", label: t.nav.partners, type: "anchor", id: "partners" },
    { href: "/#contact", label: t.nav.contact, type: "anchor", id: "contact" },
  ];

  return (
    <header data-testid="site-header" className="relative z-50">
      {/* Top strip — logo + language + CTA — scrolls away with the page */}
      <div className="bg-gradient-to-b from-[#b6d6ec]/60 via-[#b6d6ec]/25 to-transparent">
        <div className="mx-auto flex max-w-[1400px] items-start justify-between gap-6 px-6 py-3 md:px-12 md:py-4 lg:px-16">
          <LogoMark large={isHome} shrunk={scrolled} />

          <div className="flex shrink-0 items-center gap-3 self-start pt-2 md:pt-3">
            <LanguageSwitcher />
            <a
              href="/#contact"
              onClick={(e) => goToAnchor(e, "contact")}
              data-testid="header-cta-button"
              className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-blue-600 px-5 py-2 text-[14px] font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/35 active:scale-95 md:inline-flex lg:text-base"
            >
              {t.nav.cta}
              <span aria-hidden className="text-lg leading-none">→</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 md:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <List size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Sticky nav strip — stays pinned at the top while scrolling */}
      <div
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-[#b6d6ec]/90 border-b border-sky-300/60 saturate-150 shadow-sm shadow-sky-500/10"
            : "bg-[#b6d6ec]/50 backdrop-blur-md border-b border-sky-300/30"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center px-6 py-3 md:px-12 lg:px-16">
          <nav
            className="hidden items-center gap-5 md:flex lg:gap-7"
            aria-label="Primary"
            data-testid="primary-nav"
          >
            {links.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`nav-link-${l.id}`}
                  aria-current={isActive(l) ? "page" : undefined}
                  className={`nav-link origin-bottom whitespace-nowrap text-base font-semibold transition-transform duration-200 ease-out hover:scale-[1.2] lg:text-lg ${
                    isActive(l)
                      ? "nav-link-active text-blue-700"
                      : "text-slate-700 hover:text-slate-900"
                  }`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goToAnchor(e, l.id)}
                  data-testid={`nav-link-${l.id}`}
                  aria-current={isActive(l) ? "true" : undefined}
                  className={`nav-link origin-bottom whitespace-nowrap text-base font-semibold transition-transform duration-200 ease-out hover:scale-[1.2] lg:text-lg ${
                    isActive(l)
                      ? "nav-link-active text-blue-700"
                      : "text-slate-700 hover:text-slate-900"
                  }`}
                >
                  {l.label}
                </a>
              ),
            )}
            <CatalogMenu />
          </nav>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-slate-200 bg-white/95 backdrop-blur-xl md:hidden"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {links.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goToAnchor(e, l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {l.label}
                </a>
              ),
            )}
            <a
              href="/#partners"
              onClick={(e) => goToAnchor(e, "partners")}
              data-testid="mobile-nav-link-catalog"
              className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100"
            >
              {t.nav.catalog}
            </a>
            <a
              href="/#contact"
              onClick={(e) => goToAnchor(e, "contact")}
              data-testid="mobile-cta-button"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-base font-semibold text-white hover:bg-blue-500"
            >
              {t.nav.cta} →
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
